import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2, Search } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { listUsers, UserWithSites } from '@/app/services/userManagementService';
import { Party } from '@/app/types/party';

interface AssignUserToPartyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  party: Party | null;
  onAssignUsers: (userIds: string[]) => Promise<void>;
  organizationId: number;
}

const AssignUserToPartyModal: React.FC<AssignUserToPartyModalProps> = ({
  open,
  onOpenChange,
  party,
  onAssignUsers,
  organizationId,
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all users in the organization
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['organizationUsers', organizationId],
    queryFn: () => listUsers({ limit: 100 }),
    enabled: !!organizationId && open,
  });

  // Filter out users already assigned to this party and apply search
  const availableUsers = useMemo(() => {
    if (!usersData?.items) return [];

    const assignedUserIds = party?.managing_users?.map(u => u.id) || [];

    return usersData.items
      .filter(user => !assignedUserIds.includes(user.id))
      .filter(user => user.role?.name === 'client' || user.role?.name === 'client_manager') // Show client and client_manager users
      .filter(user => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          user.username.toLowerCase().includes(query) ||
          user.full_name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query)
        );
      });
  }, [usersData, party, searchQuery]);

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === availableUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(availableUsers.map(u => u.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedUserIds.length === 0) return;

    setIsSubmitting(true);
    try {
      await onAssignUsers(selectedUserIds);
      setSelectedUserIds([]);
      setSearchQuery('');
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setSearchQuery('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Assign Users to Party</DialogTitle>
          <DialogDescription>
            Select users to give them access to {party?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Users List */}
          {isLoadingUsers ? (
            <div className="flex justify-center items-center h-48">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : availableUsers.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              {searchQuery
                ? 'No users found matching your search'
                : 'No available users to assign'}
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="flex items-center space-x-2 px-2 py-1 border-b">
                <Checkbox
                  id="select-all"
                  checked={selectedUserIds.length === availableUsers.length}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                  Select All ({availableUsers.length})
                </Label>
              </div>

              {/* Users List */}
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {availableUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-start space-x-3 p-3 rounded-md hover:bg-accent cursor-pointer"
                      onClick={() => handleToggleUser(user.id)}
                    >
                      <Checkbox
                        id={`user-${user.id}`}
                        checked={selectedUserIds.includes(user.id)}
                        onCheckedChange={() => handleToggleUser(user.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 space-y-1">
                        <Label
                          htmlFor={`user-${user.id}`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          {user.full_name || user.username}
                        </Label>
                        <div className="flex flex-col gap-1">
                          <p className="text-xs text-muted-foreground">
                            {user.username}
                          </p>
                          {user.email && (
                            <p className="text-xs text-muted-foreground">
                              {user.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedUserIds.length === 0 || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Assign {selectedUserIds.length > 0 && `(${selectedUserIds.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignUserToPartyModal;
